import AdminTable from 'common/AdminTable';
import { getRequest, postRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const initialFormData = {
  name: '',
  link: '',
  image: null,
  preview: null
};

const WebsiteLinks = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest('websitelink?limit=1000');
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load website links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({
      ...prev,
      image: file,
      preview: URL.createObjectURL(file)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.link.trim()) {
      toast.error('Link is required');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name.trim());
    formDataToSend.append('link', formData.link.trim());
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    setSaving(true);
    try {
      if (editingId) {
        await patchRequest(`websitelink/${editingId}`, formDataToSend);
        toast.success('Website link updated successfully');
      } else {
        await postRequest('websitelink', formDataToSend);
        toast.success('Website link added successfully');
      }
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save website link');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      name: row.name || '',
      link: row.link || '',
      image: null,
      preview: row.image ? `${process.env.REACT_APP_BACKEND}uploads/${row.image}` : null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This website link will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await deleteRequest(`websitelink/${id}`);
        toast.success('Website link deleted successfully');
        if (editingId === id) resetForm();
        fetchRecords();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to delete website link');
      }
    });
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) => (
        <img
          src={`${process.env.REACT_APP_BACKEND}uploads/${row.original.image}`}
          alt={row.original.name}
          className="banner-thumb"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )
    },
    {
      Header: 'Link',
      accessor: 'link',
      Cell: ({ row }) => (
        <a href={row.original.link} target="_blank" rel="noopener noreferrer" className="text-primary">
          Visit
        </a>
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button className="btn btn-sm btn-info mx-1 my-1" onClick={() => handleEdit(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger mx-1 my-1" onClick={() => handleDelete(row.original.id)}>
            Delete
          </button>
        </>
      )
    }
  ];

  return (
    <div className="container-fluid px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h2 className="m-0">Website Links</h2>
          <small className="text-muted">Manage hospital website links</small>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 mb-4">
          <div className="card shadow border-0 h-100">
            <div className="card-header bg-primary text-white py-3">
              <h5 className="mb-0">{editingId ? 'Update Website Link' : 'Add Website Link'}</h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="adminlables">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    placeholder="e.g. Google Maps, Helpline"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="link" className="adminlables">
                    Link *
                  </label>
                  <input
                    type="text"
                    id="link"
                    name="link"
                    className="form-control"
                    placeholder="https://example.com/"
                    value={formData.link}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="image" className="adminlables">
                    Image {editingId ? '(Leave blank to keep current)' : ''}
                  </label>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    accept="image/*"
                    className="form-control btn-block filebg"
                    onChange={handleFile}
                  />
                </div>
                {formData.preview && (
                  <div className="mb-4">
                    <p className="adminlables mb-1">Preview</p>
                    <img
                      src={formData.preview}
                      alt="Preview"
                      style={{ maxWidth: 220, maxHeight: 140, borderRadius: 8, border: '1px solid #e2e8f0' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div className="d-flex justify-content-end gap-3">
                  {editingId && (
                    <button type="button" className="btn btn-light px-4" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary px-5" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow border-0">
            <div className="card-header bg-white py-3">
              <h5 className="mb-0">Website Link List</h5>
            </div>
            <div className="card-body p-0">
              {loading && records.length === 0 ? (
                <div className="text-center p-5">Loading...</div>
              ) : (
                <AdminTable tableHeaders={tableHeaders} tableData={records} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteLinks;
